import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';

/**
 * Native-Angular port of the AngularJS `ftScrollTo` directive
 * (account-v1/.../components/common/scroll/scroll.js).
 * `<a ft-scroll-to=".some-selector">` — click scrolls to the first
 * matching element; smooth by default (the port's established behavior).
 * `ft-scroll-behavior="instant"` opts a host into the v1 jump — v1's
 * `scroll.scrollTo(target, 1)` was a single-frame rAF "animation", i.e.
 * instant; the journey offramps key on that (they must not animate).
 */
@Directive({
    selector: '[ft-scroll-to]',
    standalone: false
})
export class FtScrollToDirective {
  @Input('ft-scroll-to') target!: string;
  @Input('ft-scroll-behavior') behavior: ScrollBehavior = 'smooth';

  @HostListener('click') onClick(): void {
    if (!this.target) return;
    const el = document.querySelector(this.target) as HTMLElement | null;
    el?.scrollIntoView({ behavior: this.behavior, block: 'start' });
  }
}

/**
 * Native-Angular port of the AngularJS `ftPositionalClass` directive
 * (account-v2/.../directives/positional-class/positionalClass.directive.ts).
 * `<div ft-positional-class="my-class" ft-positional-class-on="50%">` —
 * scroll-driven CSS class toggle. v1 uses the Waypoints library; native
 * port uses IntersectionObserver with a rootMargin derived from
 * `ftPositionalClassOn` (string like '50%' or '100px').
 *
 * For QA-login the page renders at viewport top so the waypoint never
 * triggers — no diff. When a property exercises scroll-deep content,
 * the class toggle becomes visible and IntersectionObserver matches v1's
 * direction-based add/remove behavior.
 */
@Directive({
    selector: '[ft-positional-class]',
    standalone: false
})
export class FtPositionalClassDirective implements OnInit, OnDestroy {
  @Input('ft-positional-class') addClass!: string;
  @Input('ft-positional-class-on') triggerOffset = '50%';

  private observer?: IntersectionObserver;
  private lastTop = 0;

    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

  ngOnInit(): void {
    if (!this.addClass || typeof IntersectionObserver === 'undefined') return;
    this.lastTop = this.el.nativeElement.getBoundingClientRect().top;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const currentTop = entry.boundingClientRect.top;
          const direction = currentTop > this.lastTop ? 'up' : 'down';
          if (direction === 'up') this.renderer.removeClass(this.el.nativeElement, this.addClass);
          else this.renderer.addClass(this.el.nativeElement, this.addClass);
          this.lastTop = currentTop;
        }
      },
      { rootMargin: `0px 0px -${this.triggerOffset || '50%'} 0px`, threshold: 0 },
    );
    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
